import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Tooltip,
  Alert,
  Fab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';

import departmentService from '../../services/departmentService';
import { useLanguageAwareRender } from '../../hooks/useLanguageAwareRender';
import { t } from '../../utils/localization/localization';
import { logger } from '../../utils/logger';
import { ErrorState, tableShellSx, responsiveTableContainerSx } from '../common';

export default function DepartmentManagement() {
  // Use custom hook for language-aware rendering
  const languageRenderKey = useLanguageAwareRender('DepartmentManagement');

  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userCounts, setUserCounts] = useState({});

  const departmentTableColumnSx = {
    name: { width: { xs: '21%', md: '22%' }, minWidth: { xs: 160, md: 190 }, fontWeight: 'bold' },
    description: { width: { xs: '31%', md: '33%' }, minWidth: { xs: 200, md: 280 }, fontWeight: 'bold' },
    status: { width: { xs: '13%', md: '12%' }, minWidth: { xs: 100, md: 120 }, whiteSpace: 'nowrap', fontWeight: 'bold' },
    users: { width: { xs: '11%', md: '10%' }, minWidth: { xs: 90, md: 110 }, whiteSpace: 'nowrap', fontWeight: 'bold' },
    created: { width: { xs: '13%', md: '12%' }, minWidth: { xs: 100, md: 120 }, whiteSpace: 'nowrap', fontWeight: 'bold' },
    actions: { width: { xs: '1%', md: '11%' }, minWidth: 110, whiteSpace: 'nowrap', fontWeight: 'bold' }
  };

  // Load departments
  const loadDepartments = async () => {
    try {
      setLoading(true);
      const response = await departmentService.getAllDepartments(true); // Include inactive
      logger.info('[DEPARTMENT_MANAGEMENT] Loaded departments:', response.departments);
      setDepartments(response.departments || []);

      // Load user counts for each department
      const counts = {};
      for (const dept of response.departments || []) {
        try {
          const countData = await departmentService.getDepartmentUserCount(dept.id);
          counts[dept.id] = countData.user_count;
        } catch (error) {
          logger.error('[DEPARTMENT_MANAGEMENT] Error loading user count for dept', dept.id, error);
          counts[dept.id] = 0;
        }
      }
      setUserCounts(counts);

    } catch (error) {
      logger.error('[DEPARTMENT_MANAGEMENT] Error loading departments:', error);
      setError('Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDepartments();
  }, []);

  const handleOpenDialog = (mode, department = null) => {
    setDialogMode(mode);
    setSelectedDepartment(department);

    if (mode === 'edit' && department) {
      setFormData({
        name: department.name,
        description: department.description || ''
      });
    } else {
      setFormData({
        name: '',
        description: ''
      });
    }

    setError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      // Validate form
      if (!formData.name.trim()) {
        setError(t('admin.departments.nameRequired'));
        return;
      }

      // Validate name availability
      const isAvailable = await departmentService.validateDepartmentName(
        formData.name.trim(),
        selectedDepartment?.id
      );

      if (!isAvailable) {
        setError(t('admin.departments.nameAlreadyExists'));
        return;
      }

      if (dialogMode === 'add') {
        // Create new department
        const result = await departmentService.createDepartment(
          formData.name.trim(),
          formData.description.trim()
        );

        if (result.success) {
          setSuccess(t('admin.departments.createSuccess'));
          logger.info('[DEPARTMENT_MANAGEMENT] Department created successfully');
        } else {
          setError(result.message || t('admin.departments.createError'));
          return;
        }
      } else if (dialogMode === 'edit') {
        // Update department
        const result = await departmentService.updateDepartment(
          selectedDepartment.id,
          {
            name: formData.name.trim(),
            description: formData.description.trim()
          }
        );

        if (result.success) {
          setSuccess(t('admin.departments.updateSuccess'));
          logger.info('[DEPARTMENT_MANAGEMENT] Department updated successfully');
        } else {
          setError(result.message || t('admin.departments.updateError'));
          return;
        }
      }

      // Refresh departments list
      await loadDepartments();
      handleCloseDialog();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);

    } catch (error) {
      logger.error('[DEPARTMENT_MANAGEMENT] Error submitting form:', error);
      setError(error.response?.data?.detail || error.message || t('admin.departments.submitError'));
    }
  };

  const handleDeleteDepartment = async (department) => {
    if (!window.confirm(t('admin.departments.confirmDelete', { name: department.name }))) {
      return;
    }

    try {
      const result = await departmentService.deleteDepartment(department.id);

      if (result.success) {
        setSuccess(result.message || t('admin.departments.deleteSuccess'));
        logger.info('[DEPARTMENT_MANAGEMENT] Department deleted successfully');

        // Refresh departments list
        await loadDepartments();

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.message || t('admin.departments.deleteError'));
      }

    } catch (error) {
      logger.error('[DEPARTMENT_MANAGEMENT] Error deleting department:', error);
      setError(error.response?.data?.detail || error.message || t('admin.departments.deleteError'));
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>{t('common.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box key={languageRenderKey}>
      {success && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="success" onClose={() => setSuccess('')}>
            {success}
          </Alert>
        </Box>
      )}

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          {t('admin.departments.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('admin.departments.subtitle')}
        </Typography>
      </Box>

      <Box sx={tableShellSx}>
        <TableContainer component={Paper} sx={responsiveTableContainerSx}>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: { xs: 860, md: 1040 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={departmentTableColumnSx.name}>
                  {t('admin.departments.nameColumn')}
                </TableCell>
                <TableCell sx={departmentTableColumnSx.description}>
                  {t('admin.departments.descriptionColumn')}
                </TableCell>
                <TableCell sx={departmentTableColumnSx.status}>
                  {t('admin.departments.statusColumn')}
                </TableCell>
                <TableCell sx={departmentTableColumnSx.users}>
                  {t('admin.departments.usersColumn')}
                </TableCell>
                <TableCell sx={departmentTableColumnSx.created}>
                  {t('admin.departments.createdColumn')}
                </TableCell>
                <TableCell className="table-actions-cell" sx={departmentTableColumnSx.actions}>
                  {t('common.actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {departments.map((department) => (
                <TableRow key={department.id}>
                  <TableCell sx={departmentTableColumnSx.name}>
                    <Typography variant="body1" fontWeight="medium">
                      {department.name}
                    </Typography>
                  </TableCell>
                  <TableCell sx={departmentTableColumnSx.description}>
                    <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                      {department.description || t('common.noDescription')}
                    </Typography>
                  </TableCell>
                  <TableCell sx={departmentTableColumnSx.status}>
                    <Chip
                      label={department.is_active ? t('common.active') : t('common.inactive')}
                      color={department.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={departmentTableColumnSx.users}>
                    <Tooltip title={`${userCounts[department.id] || 0} ${t('admin.departments.usersInDepartment')}`}>
                      <Chip
                        icon={<PeopleIcon />}
                        label={userCounts[department.id] || 0}
                        size="small"
                        color={userCounts[department.id] > 0 ? 'primary' : 'default'}
                        variant={userCounts[department.id] > 0 ? 'filled' : 'outlined'}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={departmentTableColumnSx.created}>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(department.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell className="table-actions-cell" sx={departmentTableColumnSx.actions}>
                    <Tooltip title={t('admin.departments.editDepartment')}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog('edit', department)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('admin.departments.deleteDepartment')}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteDepartment(department)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Add Department FAB */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16
        }}
        onClick={() => handleOpenDialog('add')}
      >
        <AddIcon />
      </Fab>

      {/* Add/Edit Department Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? t('admin.departments.addDepartment') : t('admin.departments.editDepartment')}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Box sx={{ mb: 2 }}>
              <ErrorState
                error={error}
                message={error}
                severity="error"
                component="DepartmentManagement-Dialog"
                showRetry={false}
              />
            </Box>
          )}

          <TextField
            margin="normal"
            required
            fullWidth
            label={t('admin.departments.nameLabel')}
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder={t('admin.departments.namePlaceholder')}
            autoFocus
          />

          <TextField
            margin="normal"
            fullWidth
            multiline
            rows={3}
            label={t('admin.departments.descriptionLabel')}
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder={t('admin.departments.descriptionPlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} color="primary" variant="contained">
            {dialogMode === 'add' ? t('admin.departments.createButton') : t('admin.departments.updateButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
